import { useState } from 'react';
import {
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Select,
  Badge,
  Skeleton,
  Alert,
  Card,
  SimpleGrid,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconPackage,
  IconAlertTriangle,
  IconDownload,
  IconRefresh,
  IconLink,
  IconDeviceDesktop,
  IconDeviceLaptop,
  IconPrinter,
  IconScan,
  IconPresentation,
  IconDevices,
} from '@tabler/icons-react';

import {
  useAssets,
  useAssetTypes,
  useAssetStatuses,
  useAssetSummary,
  useCreateAsset,
  useUpdateAsset,
  useDeleteAsset,
  useExportAssets,
  useSyncDevicesFromOcs,
  useSites,
  useRooms,
} from '@/hooks';
import { Asset } from '@/types';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  in_repair: 'yellow',
  for_disposal: 'orange',
  disposed: 'gray',
};

const TYPE_ICONS: Record<string, typeof IconDeviceDesktop> = {
  desktop: IconDeviceDesktop,
  laptop: IconDeviceLaptop,
  monitor: IconDevices,
  printer: IconPrinter,
  scanner: IconScan,
  projector: IconPresentation,
  other: IconPackage,
};

export function InventoryPage() {
  const [filters, setFilters] = useState({
    status: '',
    assetType: '',
    siteId: '',
    search: '',
  });

  const { data: assets, isLoading, isError, refetch } = useAssets(
    Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
  );
  const { data: assetTypes } = useAssetTypes();
  const { data: assetStatuses } = useAssetStatuses();
  const { data: summary } = useAssetSummary();
  const { data: sites } = useSites();
  const { data: rooms } = useRooms(filters.siteId || undefined);

  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const deleteAsset = useDeleteAsset();
  const exportAssets = useExportAssets();
  const syncDevices = useSyncDevicesFromOcs();

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [deletingAsset, setDeletingAsset] = useState<Asset | null>(null);

  const [formData, setFormData] = useState({
    inventoryNumber: '',
    name: '',
    assetType: 'other' as string,
    status: 'active' as string,
    siteId: '',
    roomId: '',
    purchaseDate: null as Date | null,
    notes: '',
    linkedToId: '',
  });

  const resetForm = () => {
    setFormData({
      inventoryNumber: '',
      name: '',
      assetType: 'other',
      status: 'active',
      siteId: '',
      roomId: '',
      purchaseDate: null,
      notes: '',
      linkedToId: '',
    });
    setEditingAsset(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    openModal();
  };

  const handleOpenEdit = (asset: Asset) => {
    setEditingAsset(asset);
    setFormData({
      inventoryNumber: asset.inventoryNumber,
      name: asset.name,
      assetType: asset.assetType,
      status: asset.status,
      siteId: asset.siteId || '',
      roomId: asset.roomId || '',
      purchaseDate: asset.purchaseDate ? new Date(asset.purchaseDate) : null,
      notes: asset.notes || '',
      linkedToId: asset.linkedToId || '',
    });
    openModal();
  };

  const handleOpenDelete = (asset: Asset) => {
    setDeletingAsset(asset);
    openDeleteModal();
  };

  const handleSubmit = async () => {
    if (!formData.inventoryNumber || !formData.name || !formData.assetType) {
      notifications.show({
        title: 'Error',
        message: 'Please fill in required fields',
        color: 'red',
      });
      return;
    }

    try {
      if (editingAsset) {
        await updateAsset.mutateAsync({
          id: editingAsset.id,
          data: {
            inventoryNumber: formData.inventoryNumber,
            name: formData.name,
            assetType: formData.assetType,
            status: formData.status,
            siteId: formData.siteId || null,
            roomId: formData.roomId || null,
            purchaseDate: formData.purchaseDate?.toISOString().split('T')[0] || null,
            notes: formData.notes || null,
            linkedToId: formData.linkedToId || null,
          },
        });
        notifications.show({
          title: 'Asset updated',
          message: `${formData.name} has been updated`,
          color: 'green',
        });
      } else {
        await createAsset.mutateAsync({
          inventoryNumber: formData.inventoryNumber,
          name: formData.name,
          assetType: formData.assetType,
          status: formData.status,
          siteId: formData.siteId || undefined,
          roomId: formData.roomId || undefined,
          purchaseDate: formData.purchaseDate?.toISOString().split('T')[0],
          notes: formData.notes || undefined,
          linkedToId: formData.linkedToId || undefined,
        });
        notifications.show({
          title: 'Asset created',
          message: `${formData.name} has been added to inventory`,
          color: 'green',
        });
      }
      closeModal();
      resetForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save asset',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingAsset) return;

    try {
      await deleteAsset.mutateAsync(deletingAsset.id);
      notifications.show({
        title: 'Asset deleted',
        message: `${deletingAsset.name} has been removed`,
        color: 'green',
      });
      closeDeleteModal();
      setDeletingAsset(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete asset',
        color: 'red',
      });
    }
  };

  const handleExport = async () => {
    try {
      await exportAssets.mutateAsync(
        Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      );
      notifications.show({
        title: 'Export complete',
        message: 'Excel file has been downloaded',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Export failed',
        message: error instanceof Error ? error.message : 'Failed to export',
        color: 'red',
      });
    }
  };

  const handleSyncDevices = async () => {
    try {
      const result = await syncDevices.mutateAsync();
      notifications.show({
        title: 'Sync complete',
        message: result.message,
        color: 'green',
      });
      refetch();
    } catch (error) {
      notifications.show({
        title: 'Sync failed',
        message: error instanceof Error ? error.message : 'Failed to sync',
        color: 'red',
      });
    }
  };

  const siteOptions = sites?.map((site) => ({
    value: site.id,
    label: `${site.code} - ${site.name}`,
  })) || [];

  const roomOptions = rooms?.map((room) => ({
    value: room.id,
    label: `${room.roomCode} - ${room.roomName}`,
  })) || [];

  const typeOptions = assetTypes?.map((type) => ({
    value: type.value,
    label: type.label,
  })) || [];

  const statusOptions = assetStatuses?.map((status) => ({
    value: status.value,
    label: status.label,
  })) || [];

  // Assets that can be linked to (computers)
  const linkableAssets = assets?.filter(a =>
    (a.assetType === 'desktop' || a.assetType === 'laptop') &&
    (!editingAsset || a.id !== editingAsset.id)
  ) || [];

  return (
    <>
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Inventory</Title>
            <Text c="dimmed" size="sm">
              Asset management for accounting
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={handleSyncDevices}
              loading={syncDevices.isPending}
            >
              Sync from OCS
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleExport}
              loading={exportAssets.isPending}
            >
              Export Excel
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
              Add Asset
            </Button>
          </Group>
        </Group>

        {/* Summary cards */}
        {summary && (
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Card withBorder>
              <Text size="sm" c="dimmed">Total Assets</Text>
              <Text size="xl" fw={700}>{summary.total}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">Active</Text>
              <Text size="xl" fw={700} c="green">{summary.byStatus.active || 0}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">For Disposal</Text>
              <Text size="xl" fw={700} c="orange">{summary.byStatus.for_disposal || 0}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">Disposed</Text>
              <Text size="xl" fw={700} c="gray">{summary.byStatus.disposed || 0}</Text>
            </Card>
          </SimpleGrid>
        )}

        {/* Filters */}
        <Paper withBorder p="md">
          <Group>
            <TextInput
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              style={{ flex: 1, maxWidth: 250 }}
            />
            <Select
              placeholder="Type"
              data={[{ value: '', label: 'All types' }, ...typeOptions]}
              value={filters.assetType}
              onChange={(v) => setFilters(f => ({ ...f, assetType: v || '' }))}
              clearable
              w={150}
            />
            <Select
              placeholder="Status"
              data={[{ value: '', label: 'All statuses' }, ...statusOptions]}
              value={filters.status}
              onChange={(v) => setFilters(f => ({ ...f, status: v || '' }))}
              clearable
              w={150}
            />
            <Select
              placeholder="Site"
              data={[{ value: '', label: 'All sites' }, ...siteOptions]}
              value={filters.siteId}
              onChange={(v) => setFilters(f => ({ ...f, siteId: v || '' }))}
              clearable
              w={180}
            />
          </Group>
        </Paper>

        {/* Assets table */}
        <Paper withBorder>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert color="red" m="md">
              Failed to load assets
            </Alert>
          ) : assets?.length === 0 ? (
            <Stack align="center" p="xl" gap="md">
              <IconPackage size={48} color="gray" />
              <Text c="dimmed">No assets in inventory</Text>
              <Group>
                <Button variant="light" onClick={handleSyncDevices}>
                  Sync from OCS
                </Button>
                <Button variant="light" onClick={handleOpenCreate}>
                  Add manually
                </Button>
              </Group>
            </Stack>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Inv. Number</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Site</Table.Th>
                  <Table.Th>Room</Table.Th>
                  <Table.Th>Age</Table.Th>
                  <Table.Th>Linked To</Table.Th>
                  <Table.Th w={100}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {assets?.map((asset) => {
                  const TypeIcon = TYPE_ICONS[asset.assetType] || IconPackage;
                  return (
                    <Table.Tr key={asset.id}>
                      <Table.Td>
                        <Text fw={500} ff="monospace" size="sm">
                          {asset.inventoryNumber}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <TypeIcon size={16} />
                          <div>
                            <Text size="sm">{asset.name}</Text>
                            {asset.isFromOcs && (
                              <Text size="xs" c="dimmed">OCS</Text>
                            )}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">
                          {asset.assetTypeLabel}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          size="sm"
                          color={STATUS_COLORS[asset.status] || 'gray'}
                        >
                          {asset.statusLabel}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{asset.siteName || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{asset.roomName || '-'}</Text>
                      </Table.Td>
                      <Table.Td>
                        {asset.age !== null ? (
                          <Text size="sm">{asset.age} yr</Text>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {asset.linkedToName ? (
                          <Tooltip label={asset.linkedToName}>
                            <Group gap={4}>
                              <IconLink size={14} />
                              <Text size="sm" truncate maw={100}>
                                {asset.linkedToName}
                              </Text>
                            </Group>
                          </Tooltip>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleOpenEdit(asset)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="red"
                            onClick={() => handleOpenDelete(asset)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Create/Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          closeModal();
          resetForm();
        }}
        title={editingAsset ? 'Edit Asset' : 'Add Asset'}
        size="lg"
      >
        <Stack gap="md">
          <Group grow>
            <TextInput
              label="Inventory Number"
              placeholder="INV-001"
              required
              value={formData.inventoryNumber}
              onChange={(e) => setFormData({ ...formData, inventoryNumber: e.target.value })}
              disabled={editingAsset?.isFromOcs}
            />
            <Select
              label="Type"
              placeholder="Select type"
              required
              data={typeOptions}
              value={formData.assetType}
              onChange={(v) => setFormData({ ...formData, assetType: v || 'other' })}
            />
          </Group>

          <TextInput
            label="Name"
            placeholder="Dell Monitor 24inch"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Group grow>
            <Select
              label="Status"
              data={statusOptions}
              value={formData.status}
              onChange={(v) => setFormData({ ...formData, status: v || 'active' })}
            />
            <DateInput
              label="Purchase Date"
              placeholder="Select date"
              value={formData.purchaseDate}
              onChange={(v: Date | null) => setFormData({ ...formData, purchaseDate: v })}
              clearable
            />
          </Group>

          <Group grow>
            <Select
              label="Site"
              placeholder="Select site"
              data={siteOptions}
              value={formData.siteId}
              onChange={(v) => setFormData({ ...formData, siteId: v || '', roomId: '' })}
              clearable
            />
            <Select
              label="Room"
              placeholder="Select room"
              data={roomOptions}
              value={formData.roomId}
              onChange={(v) => setFormData({ ...formData, roomId: v || '' })}
              clearable
              disabled={!formData.siteId}
            />
          </Group>

          {(formData.assetType === 'monitor' || formData.assetType === 'printer' || formData.assetType === 'scanner') && (
            <Select
              label="Linked To (Computer)"
              placeholder="Select computer this belongs to"
              data={linkableAssets.map(a => ({ value: a.id, label: `${a.inventoryNumber} - ${a.name}` }))}
              value={formData.linkedToId}
              onChange={(v) => setFormData({ ...formData, linkedToId: v || '' })}
              clearable
              searchable
            />
          )}

          <Textarea
            label="Notes"
            placeholder="Additional notes..."
            minRows={2}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeModal(); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createAsset.isPending || updateAsset.isPending}
            >
              {editingAsset ? 'Save Changes' : 'Add Asset'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeletingAsset(null);
        }}
        title="Delete Asset"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            Are you sure you want to delete <strong>{deletingAsset?.name}</strong>?
            {deletingAsset && deletingAsset.linkedAssetsCount > 0 && (
              <Text size="sm" mt="xs">
                This asset has {deletingAsset.linkedAssetsCount} linked asset(s).
              </Text>
            )}
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { closeDeleteModal(); setDeletingAsset(null); }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteAsset.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
